import { Button } from '@sunwebgroup/uds-ui/button'
import { InputStepper } from '@sunwebgroup/uds-ui/input-stepper'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import { sub } from 'date-fns'
import { partial } from 'ramda'
import * as React from 'react'
import { use } from 'react'

import {
  BABIES_DEFAULT_AGE,
  BABIES_MAX_AGE,
  BABIES_MIN_AGE,
  CHILDREN_DEFAULT_AGE,
  CHILDREN_MAX_AGE,
  CHILDREN_MIN_AGE,
} from '@/domain/shared/constants'
import { useIntl } from '@/intl'
import { CatalogueDetailsContext } from '@/scopes/common/components/CatalogueDetailsContext'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import DateDropdown from '../../DateDropdown'
import styles from './Room.module.css'
import { useRoomsParticipantsDistributionStore } from './useParticipantsStore'

const MAX_ADULTS = 19
const MAX_CHILDREN = 19
const MAX_BABIES = 19

export default function Room({ roomIndex }: { roomIndex: number }) {
  const intl = useIntl()

  const {
    setAdults,
    addChild,
    setChild,
    removeChild,
    addBaby,
    setBaby,
    removeBaby,
    deleteRoom,
    roomsParticipantDistribution,
  } = useRoomsParticipantsDistributionStore()

  const { isWinter } = use(CatalogueDetailsContext)

  const dateFormat = useConfiguration('dateFormat')
  const birthdateReferenceDate = new Date()
  const childBirthdateMin = sub(birthdateReferenceDate, { years: CHILDREN_MAX_AGE })
  const childBirthdateMax = sub(birthdateReferenceDate, { years: CHILDREN_MIN_AGE, days: 1 })
  const babyBirthdateMin = sub(birthdateReferenceDate, { years: BABIES_MAX_AGE })
  const babyBirthdateMax = sub(birthdateReferenceDate, { years: BABIES_MIN_AGE, days: 1 })

  const { adults, babies, children } = roomsParticipantDistribution[roomIndex]

  const isFirstRoom = roomIndex === 0

  const handleAdultsChange = partial(setAdults, [roomIndex])

  const handleSetChildrenDate = (index: number) => (date: Date) => {
    setChild(roomIndex, index, new Date(date))
  }

  const handleChildChange = (newValue: number) => {
    if (newValue > children.length) {
      addChild(roomIndex, sub(new Date(), { years: CHILDREN_DEFAULT_AGE, days: 1 }))
    }
    else if (newValue < children.length) {
      removeChild(roomIndex)
    }
  }

  const handleSetBabiesDate = (index: number) => (date: Date) => {
    setBaby(roomIndex, index, date)
  }

  const handleBabyChange = (newValue: number) => {
    if (newValue > babies.length) {
      addBaby(roomIndex, sub(new Date(), { years: BABIES_DEFAULT_AGE, days: 1 }))
    }
    else if (newValue < babies.length) {
      removeBaby(roomIndex)
    }
  }

  const handleRoomDeleteClick = () => deleteRoom(roomIndex)

  return (
    <div className={styles.wrapper}>
      <div className={styles.roomTitleWrapper}>
        <Typography variant="body-l-bold-tight" className={styles.roomTitle}>
          {intl.formatMessage({
            id: isWinter
              ? 'quicksearch_filter_participants_apartment_singular'
              : 'quicksearch_filter_participants_room_singular',
          })}
          {' '}
          {roomIndex + 1}
        </Typography>
        {!isFirstRoom && (
          <Button variant="transparentPrimary" size="S" onClick={handleRoomDeleteClick}>
            {intl.formatMessage({
              id: 'quicksearch_filter_participants_delete_room',
            })}
          </Button>
        )}
      </div>
      <div className={styles.item} data-qa={`adults-number-stepper-room-${roomIndex}`}>
        <div className={styles.labelWrapper}>
          <Typography variant="body-m-regular-tight">
            {intl.formatMessage({
              id: 'quicksearch_filter_participants_adults',
            })}
          </Typography>
          <Typography variant="body-s-regular-tight" className={styles.subLabel}>
            {intl.formatMessage(
              { id: 'quicksearch_filter_participants_adults_description' },
              { min_age: CHILDREN_MAX_AGE },
            )}
          </Typography>
        </div>
        <InputStepper
          initialValue={adults}
          size="M"
          minimumValue={isFirstRoom ? 1 : 0}
          maximumValue={MAX_ADULTS}
          onChange={handleAdultsChange}
          disableInput
        />
      </div>
      <div className={styles.item}>
        <div className={styles.labelWrapper}>
          <Typography variant="body-m-regular-tight">
            {intl.formatMessage({
              id: 'quicksearch_filter_participants_children',
            })}
          </Typography>
          <Typography variant="body-s-regular-tight" className={styles.subLabel}>
            {intl.formatMessage(
              { id: 'quicksearch_filter_participants_minors_description' },
              { min_age: CHILDREN_MIN_AGE, max_age: CHILDREN_MAX_AGE - 1 },
            )}
          </Typography>
        </div>
        <InputStepper
          initialValue={children.length}
          size="M"
          maximumValue={MAX_CHILDREN}
          onChange={handleChildChange}
          disableInput
        />
      </div>
      {children.map((date: Date, index: number) => (
        <div className={cn(styles.item, styles.itemDate)} key={index}>
          <Typography variant="body-s-regular-tight">
            {intl.formatMessage({
              id: 'quicksearch_filter_participants_birthdate_child',
            })}
            {' '}
            {index + 1}
          </Typography>
          <DateDropdown
            value={date}
            onValueChange={handleSetChildrenDate(index)}
            min={childBirthdateMin}
            max={childBirthdateMax}
            dateFormat={dateFormat}
          />
        </div>
      ))}
      <div className={styles.item}>
        <div className={styles.labelWrapper}>
          <Typography variant="body-m-regular-tight">
            {intl.formatMessage({
              id: 'quicksearch_filter_participants_babies',
            })}
          </Typography>
          <Typography variant="body-s-regular-tight" className={styles.subLabel}>
            {intl.formatMessage(
              { id: 'quicksearch_filter_participants_minors_description' },
              { min_age: BABIES_MIN_AGE, max_age: BABIES_MAX_AGE - 1 },
            )}
          </Typography>
        </div>
        <InputStepper
          initialValue={babies.length}
          size="M"
          maximumValue={MAX_BABIES}
          onChange={handleBabyChange}
          disableInput
        />
      </div>
      {babies.map((date: Date, index: number) => (
        <div className={cn(styles.item, styles.itemDate)} key={index}>
          <Typography variant="body-s-regular-tight">
            {intl.formatMessage({
              id: 'quicksearch_filter_participants_birthdate_baby',
            })}
            {' '}
            {index + 1}
          </Typography>
          <DateDropdown
            value={date}
            onValueChange={handleSetBabiesDate(index)}
            min={babyBirthdateMin}
            max={babyBirthdateMax}
            dateFormat={dateFormat}
          />
        </div>
      ))}
    </div>
  )
}
